import axios, { AxiosError } from "axios";

// ----------------------------------------------------------------------

export interface ApiErrorResponse {
  message?: string;
  error?: string;
  statusCode?: number;
  /** Machine-readable error code the endpoint attached, if any. */
  code?: string;
}

/**
 * An error body plus whatever extra context the endpoint attached alongside the
 * code (offending ids, filenames, counts).
 *
 * The open half is kept OUT of `ApiErrorResponse` deliberately. An index
 * signature on the interface itself makes every typo a valid `unknown` read
 * instead of a compile error — `error.data?.mesage` would type-check — and this
 * workspace is the one on TS 6 with `--max-warnings 0`, so that trade buys
 * nothing. A consumer of the extra context takes `unknown` and re-narrows from
 * scratch anyway.
 */
export type ApiErrorBody = ApiErrorResponse & Record<string, unknown>;

/**
 * Error the response interceptor rejects with.
 *
 * `message` is composed by exactly the same expression it always was, so every
 * existing `toast.error(error.message)` caller renders what it did before. The
 * subclass only *adds* the parsed response body, which endpoints returning
 * structured rejections (missing filenames, offending ids) need in the
 * component that has to render them.
 */
export class ApiError extends Error {
  readonly statusCode?: number;
  readonly code?: string;
  /** Full parsed response body, or `undefined` for network/timeout failures. */
  readonly data?: ApiErrorBody;
  readonly originalError: AxiosError<ApiErrorBody>;

  constructor(message: string, originalError: AxiosError<ApiErrorBody>) {
    super(message);
    this.name = "ApiError";
    this.statusCode = originalError.response?.status;
    this.code = originalError.response?.data?.code;
    this.data = originalError.response?.data;
    this.originalError = originalError;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * The endpoints that are reachable WITHOUT a session, by design.
 *
 * Everything else this app talks to is protected — `/admins/*`, `/agencies/*`,
 * `/organisations/*`, `/venues/*` — so this list is the whole exemption set,
 * and the request interceptor refuses anything outside it that arrives with no
 * token.
 *
 * Matched with `includes` rather than an exact compare, which is what the
 * interceptor always did: the reset-password screen posts to
 * `/auth/admin/signup` and `/auth/admin/reset-password` while deliberately
 * logged out, and login/forgot-password likewise.
 */
const UNAUTHENTICATED_ENDPOINTS = [
  "/auth/admin/login",
  "/auth/admin/signup",
  "/auth/admin/forgot-password",
  "/auth/admin/reset-password",
] as const;

/** Whether `url` is one of the endpoints that needs no session. */
export function isUnauthenticatedEndpoint(url: string | undefined): boolean {
  return (
    !!url &&
    UNAUTHENTICATED_ENDPOINTS.some((endpoint) => url.includes(endpoint))
  );
}

/**
 * Rejection for a protected request attempted with no `sessionJwt` in hand.
 *
 * The request interceptor used to omit the `Authorization` header and send the
 * call anyway, so a protected endpoint received an anonymous request and
 * answered 401 — wasteful at best, and actively wrong during logout. The
 * agency clients list polls every 3s while a disconnect is in flight (see
 * `useAgencyClients`), so a tick landing in the gap between `logout()`
 * clearing localStorage and the router leaving the screen fired an
 * unauthenticated read whose 401 then assigned `window.location.href`,
 * replacing the in-app navigation with a full page reload.
 *
 * Carries `statusCode` 401 — the answer the server would have given — so the
 * retry policy in `lib/queryRetry.ts` treats it as the verdict it is rather
 * than re-firing it.
 *
 * Deliberately does NOT redirect. Driving the browser to `/login` from the
 * request path is what created the reload race above; the redirect stays with
 * the two places that can do it without racing — every protected route's
 * `beforeLoad` guard, and the 401 branch of the response interceptor below.
 */
export class MissingSessionError extends Error {
  /** Mirrors the status the protected endpoint would have answered. */
  readonly statusCode = 401;
  /** The request that was never sent, for debugging. */
  readonly url?: string;

  constructor(url?: string) {
    super("Your session has ended. Please sign in again.");
    this.name = "MissingSessionError";
    this.url = url;
  }
}

export function isMissingSessionError(
  error: unknown,
): error is MissingSessionError {
  return error instanceof MissingSessionError;
}

// Create an Axios instance with the base URL from environment variables
const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor: Add auth token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    // Skip auth header for unauthenticated auth endpoints.
    if (isUnauthenticatedEndpoint(config.url)) {
      return config;
    }

    const token = localStorage.getItem("sessionJwt");
    if (!token) {
      // Protected endpoint with no session: fail here instead of sending an
      // anonymous request the server can only answer 401. Rejecting from the
      // request interceptor means nothing leaves the browser.
      return Promise.reject(new MissingSessionError(config.url));
    }

    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: Handle global errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    /**
     * Pass through anything that is not an Axios failure.
     *
     * Axios runs request and response interceptors in ONE promise chain, so a
     * rejection from the request interceptor above — a `MissingSessionError`
     * for a protected call with no token — lands here as well, having never
     * touched the network. Wrapping it would log an "API Error" with every
     * field `undefined` and hand back an `ApiError` whose `statusCode` is
     * `undefined`, which `retryUnlessClientError` then reads as retryable.
     */
    if (!axios.isAxiosError<ApiErrorBody>(error)) {
      return Promise.reject(error);
    }

    // Log the error with fallback
    console.error("API Error:", {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
    });

    if (error.response?.status === 401) {
      // Handle unauthorized: Clear tokens and redirect to login
      localStorage.removeItem("sessionJwt");
      localStorage.removeItem("sessionToken");
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.includes("/login")
      ) {
        window.location.href = "/login";
      }
    }

    // Extract error message from response
    const errorData = error.response?.data;
    const errorMessage =
      errorData?.message ||
      errorData?.error ||
      error.message ||
      "An unexpected error occurred";

    // Create a new error with the message
    return Promise.reject(new ApiError(errorMessage, error));
  },
);

export { axiosInstance };
